import React from 'react';
import { Link } from 'react-router-dom';
import { useGuidesBase } from '../../hooks/useGuidesBase';
import { Box, Title, Paper, Text, Stack, Group, Badge, Anchor, Alert, ThemeIcon, Table, Breadcrumbs } from '@mantine/core';
import { Lightbulb, Warning, Check, X } from '@phosphor-icons/react';
import SEO from '../../components/SEO';

export default function BiddingGuide() {
  const guidesBase = useGuidesBase();
  return (
    <Box px="lg" py="md">
      <SEO
        title="Strategie ustalania stawek w Google Ads"
        description="Manual CPC, Maximize Conversions, Target CPA, Target ROAS. Porównanie strategii licytacji i kiedy stosować każdą z nich."
        path={`${guidesBase}/bidding`}
        noIndex={guidesBase !== '/poradniki'}
      />
      <Breadcrumbs mb="sm" styles={{ separator: { color: 'var(--mantine-color-gray-4)' } }}>
        <Anchor component={Link} to={guidesBase} size="sm">Przewodniki</Anchor>
        <Text size="sm" c="dark">Strategie ustalania stawek</Text>
      </Breadcrumbs>
      <Title order={3} mb="md">Strategie ustalania stawek</Title>

      <Alert color="blue" mb="md" icon={<Lightbulb size={16} />}>
        <Text size="sm">Wybór strategii stawek ma bezpośredni wpływ na koszty i wyniki kampanii. Droid obsługuje wszystkie strategie: Manual CPC, Enhanced CPC, Maximize Conversions, Maximize Clicks, Target CPA i Target ROAS. Strategię możesz zmienić w ustawieniach kampanii.</Text>
      </Alert>

      {/* Strategy comparison */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Porównanie strategii</Text>
        <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Strategia</Table.Th>
              <Table.Th>Cel</Table.Th>
              <Table.Th>Min. danych</Table.Th>
              <Table.Th>Kontrola</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {[
              ['Manual CPC', 'Pełna kontrola stawek', 'Brak wymagań', 'Pełna'],
              ['Enhanced CPC', 'CPC + automatyczna korekta', '15+ konwersji/mc', 'Wysoka'],
              ['Maximize Clicks', 'Jak najwięcej kliknięć', 'Brak wymagań', 'Niska'],
              ['Maximize Conversions', 'Jak najwięcej konwersji', '15-30 konw./mc', 'Niska'],
              ['Target CPA', 'Docelowy koszt konwersji', '30+ konwersji/mc', 'Średnia'],
              ['Target ROAS', 'Docelowy zwrot z reklam', '50+ konwersji/mc', 'Średnia'],
            ].map(([name, goal, data, control], i) => (
              <Table.Tr key={i}>
                <Table.Td><Text size="sm" fw={500}>{name}</Text></Table.Td>
                <Table.Td><Text size="sm">{goal}</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">{data}</Text></Table.Td>
                <Table.Td><Badge size="xs" variant="light" color={control === 'Pełna' ? 'green' : control === 'Wysoka' ? 'blue' : control === 'Średnia' ? 'yellow' : 'red'}>{control}</Badge></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* When to use what */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Kiedy używać jakiej strategii?</Text>
        <Stack gap="md">
          <Box>
            <Text size="sm" fw={500} mb={4}>Nowe konto (0-15 konwersji/mc)</Text>
            <Text size="sm" c="dimmed" lh={1.6}>
              Zacznij od <strong>Manual CPC</strong> lub <strong>Enhanced CPC</strong>. Zbierz dane o tym, które słowa kluczowe konwertują. Droid automatycznie optymalizuje stawki CPC w tym trybie.
            </Text>
          </Box>
          <Box>
            <Text size="sm" fw={500} mb={4}>Zbierasz konwersje (15-50/mc)</Text>
            <Text size="sm" c="dimmed" lh={1.6}>
              Przejdź na <strong>Maximize Conversions</strong>. Algorytm Google ma wystarczająco danych, by automatycznie ustalać stawki. Ustaw limit max CPC, żeby nie przepłacać.
            </Text>
          </Box>
          <Box>
            <Text size="sm" fw={500} mb={4}>Stabilne konto (50+ konwersji/mc)</Text>
            <Text size="sm" c="dimmed" lh={1.6}>
              Użyj <strong>Target CPA</strong> lub <strong>Target ROAS</strong>. Ustaw docelowy koszt konwersji lub zwrot z wydatków. Algorytm będzie dążyć do tego celu.
            </Text>
          </Box>
        </Stack>
      </Paper>

      {/* Learning period */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Okres uczenia (Learning Period)</Text>
        <Text size="sm" c="dimmed" lh={1.6} mb="sm">
          Po zmianie strategii Google Ads wchodzi w "okres uczenia" trwający 1-2 tygodnie. W tym czasie:
        </Text>
        <Stack gap="xs">
          {[
            { ok: false, text: 'Wyniki mogą być gorsze niż przed zmianą' },
            { ok: false, text: 'NIE zmieniaj stawek ani budżetu w tym okresie' },
            { ok: false, text: 'NIE wracaj do starej strategii po 2-3 dniach' },
            { ok: true, text: 'Poczekaj minimum 14 dni przed oceną wyników' },
            { ok: true, text: 'Upewnij się, że budżet nie jest ograniczony (limited by budget)' },
            { ok: true, text: 'Monitoruj, ale nie ingeruj' },
          ].map((item, i) => (
            <Group key={i} gap="xs" wrap="nowrap">
              <ThemeIcon variant="light" color={item.ok ? 'green' : 'red'} size="xs" radius="xl">
                {item.ok ? <Check size={10} weight="bold" /> : <X size={10} weight="bold" />}
              </ThemeIcon>
              <Text size="sm">{item.text}</Text>
            </Group>
          ))}
        </Stack>
      </Paper>

      {/* Droid + bidding */}
      <Paper withBorder p="lg" radius="md">
        <Text size="sm" fw={600} mb="sm">Jak Droid współpracuje z różnymi strategiami</Text>
        <Stack gap="xs">
          {[
            'Manual CPC / Enhanced CPC: Droid automatycznie optymalizuje stawki CPC na poziomie grup reklam i słów kluczowych',
            'Maximize Conversions / Target CPA: Droid skupia się na optymalizacji słów kluczowych, treści reklam i wykluczeń (stawki zarządza Google)',
            'Target ROAS: Droid monitoruje ROAS i sugeruje zmiany budżetu między kampaniami',
            'Niezależnie od strategii: Droid zawsze optymalizuje treści reklam, słowa kluczowe i wykluczenia',
          ].map((tip, i) => (
            <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
              <ThemeIcon variant="light" color="brand" size="xs" radius="xl"><Check size={10} weight="bold" /></ThemeIcon>
              <Text size="sm" lh={1.5}>{tip}</Text>
            </Group>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}
